import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FilePlus, FileText, TrendingUp, Clock, AlertCircle, CheckCircle2 } from "lucide-react";
import { api } from "../api.js";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card.js";
import { Button } from "../components/ui/button.js";
import { Badge } from "../components/ui/badge.js";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "../components/ui/table.js";

interface Documento {
  id: number;
  tipoDte: number;
  folio: number | null;
  estado: string;
  receptorNombre: string | null;
  montoTotal: number;
  fechaEmision: string;
}

export default function DashboardPage({ onError }: { onError: (e: string) => void }) {
  const navigate = useNavigate();
  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api<Documento[]>("/documentos").catch(() => []).then((docs) => {
      setDocumentos(docs.slice(0, 5));
      setLoading(false);
    });
  }, []);

  const hoy = new Date().toISOString().slice(0, 10);
  const docsHoy = documentos.filter((d) => d.fechaEmision?.startsWith(hoy));
  const totalHoy = docsHoy.reduce((sum, d) => sum + (d.montoTotal ?? 0), 0);
  const pendientes = documentos.filter((d) => d.estado === "BORRADOR").length;
  const errores = documentos.filter((d) => d.estado === "ERROR").length;

  const stats = [
    {
      label: "Emitido hoy",
      value: `$${totalHoy.toLocaleString("es-CL")}`,
      icon: TrendingUp,
      description: `${docsHoy.length} documento(s)`,
    },
    {
      label: "Pendientes",
      value: String(pendientes),
      icon: Clock,
      description: "Borradores sin confirmar",
    },
    {
      label: "Errores",
      value: String(errores),
      icon: AlertCircle,
      description: "Documentos con error",
    },
    {
      label: "Total documentos",
      value: String(documentos.length),
      icon: FileText,
      description: "Histórico",
    },
  ];

  const estadoBadge = (estado: string) => {
    switch (estado) {
      case "EMITIDO":
      case "EMITIDO_SIN_PDF":
      case "EMITIDO_SIN_HTML":
      case "ACEPTADO":
        return <Badge variant="success">{estado === "EMITIDO" ? "Emitido" : estado === "ACEPTADO" ? "Aceptado" : "Emitido*"}</Badge>;
      case "ANULADO":
        return <Badge variant="destructive">Anulado</Badge>;
      case "RECHAZADO":
        return <Badge variant="destructive">Rechazado</Badge>;
      case "ERROR":
        return <Badge variant="destructive">Error</Badge>;
      case "BORRADOR":
      case "PENDIENTE":
      case "QUEUED":
        return <Badge variant="warning">{estado === "BORRADOR" ? "Borrador" : estado === "QUEUED" ? "En cola" : "Pendiente"}</Badge>;
      default:
        return <Badge variant="secondary">{estado}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <Card key={stat.label}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {stat.label}
              </CardTitle>
              <stat.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stat.value}</div>
              <p className="text-xs text-muted-foreground mt-1">{stat.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="md:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">Atajos</CardTitle>
            <CardDescription>Acciones rápidas</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button
              className="w-full justify-start"
              variant="outline"
              onClick={() => navigate("/emitir")}
            >
              <FilePlus className="mr-2 h-4 w-4" />
              Emitir documento
            </Button>
            <Button
              className="w-full justify-start"
              variant="outline"
              onClick={() => navigate("/emitidos")}
            >
              <FileText className="mr-2 h-4 w-4" />
              Ver documentos
            </Button>
          </CardContent>
        </Card>

        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Documentos recientes</CardTitle>
            <CardDescription>Últimos 5 documentos</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-muted-foreground">Cargando...</p>
            ) : documentos.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <CheckCircle2 className="h-8 w-8 text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">No hay documentos aún</p>
                <Button variant="outline" className="mt-3" onClick={() => navigate("/emitir")}>
                  Emitir primer documento
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Receptor</TableHead>
                    <TableHead>Monto</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Fecha</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documentos.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell className="font-medium">
                        {doc.tipoDte === 39 ? "BE" : doc.tipoDte === 41 ? "BE Exenta" : doc.tipoDte === 69 ? "BHE" : doc.tipoDte === 33 ? "Factura" : doc.tipoDte === 34 ? "Factura Exenta" : doc.tipoDte === 61 ? "NC" : `DTE ${doc.tipoDte}`}
                      </TableCell>
                      <TableCell>{doc.receptorNombre || "SII Boleta"}</TableCell>
                      <TableCell>${(doc.montoTotal ?? 0).toLocaleString("es-CL")}</TableCell>
                      <TableCell>{estadoBadge(doc.estado)}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {new Date(doc.fechaEmision).toLocaleDateString("es-CL")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
